import { useTranslation } from "react-i18next";
import SEOHead from "../../components/shared/SEOHead.jsx";
import Breadcrumbs from "../../components/shared/Breadcrumbs.jsx";
import JsonLd from "../../components/shared/JsonLd.jsx";
import { buildBreadcrumbJsonLd } from "../../utils/jsonld.js";
import { ABOUT_PATH, SUBPAGE_PATH } from "./paths.js";
import GesellschaftsvertragDe from "./Gesellschaftsvertrag.jsx";
import { VerhaltenskodexDe, VerhaltenskodexEn } from "./Verhaltenskodex.jsx";
import { JugendschutzkonzeptDe, JugendschutzkonzeptEn, JugendschutzAnhaenge } from "./Jugendschutzkonzept.jsx";
import { REPORTS } from "./reports.js";
import { HISTORY } from "./history.js";
import { TEAM } from "./team.js";
import TeamTile from "./TeamTile.jsx";

const CONDUCT_CONTENT = { de: VerhaltenskodexDe, en: VerhaltenskodexEn };
const JUGENDSCHUTZ_CONTENT = { de: JugendschutzkonzeptDe, en: JugendschutzkonzeptEn };

/**
 * team/history — still placeholder content only (no fabricated bios or
 * historical claims about the real association; same treatment as
 * Datenschutz/AGB). charter/Gesellschaftsvertrag has real content — see
 * Gesellschaftsvertrag.jsx. reports/Rechenschaftsberichte reads from
 * reports.js, currently empty — no reports exist yet.
 */
export default function AboutSubpage({ lang, topic }) {
  const { t } = useTranslation();
  const otherLang = lang === "de" ? "en" : "de";
  const path = `/${ABOUT_PATH[lang]}/${SUBPAGE_PATH[topic][lang]}`;
  const altPath = `/${ABOUT_PATH[otherLang]}/${SUBPAGE_PATH[topic][otherLang]}`;
  const breadcrumbItems = [
    { name: t("nav.home"), path: "/" },
    { name: t("nav.ueberUns"), path: `/${ABOUT_PATH[lang]}` },
    { name: t(`about.${topic}.title`), path },
  ];

  return (
    <>
      <SEOHead
        lang={lang}
        path={path}
        altPath={altPath}
        title={t(`about.${topic}.title`)}
        description={topic === "jugendschutz" ? t("about.jugendschutz.description") : undefined}
        noindex={topic !== "jugendschutz"}
      />
      <JsonLd data={buildBreadcrumbJsonLd(breadcrumbItems, lang)} />
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div className="tl-container" style={{ display: "flex", flexDirection: "column", gap: "32px", padding: "32px 40px 96px", maxWidth: "820px" }}>
          <Breadcrumbs items={breadcrumbItems} lang={lang} />
          <h1 className="tl-hero-h1" style={{ fontFamily: "var(--font-serif)", fontSize: "34px", color: "var(--color-cream)", margin: 0 }}>
            {t(`about.${topic}.title`)}
          </h1>

          {topic === "team" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
              <p style={{ fontSize: "16px", lineHeight: 1.7, color: "var(--color-cream-soft)", margin: 0 }}>{t("about.team.placeholder")}</p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "20px" }}>
                {TEAM[lang].map((member, i) => (
                  <TeamTile key={i} member={member} rolePlaceholder={t("about.team.rolePlaceholder")} />
                ))}
              </div>
            </div>
          )}

          {topic === "history" && (
            <div style={{ display: "flex", flexDirection: "column" }}>
              {HISTORY[lang].map((entry) => (
                <div
                  key={entry.date}
                  style={{ display: "grid", gridTemplateColumns: "110px 1fr", gap: "24px", padding: "20px 0", borderTop: "1px solid rgba(254,250,239,0.1)" }}
                >
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: "13px", color: "var(--color-gold)" }}>{entry.date}</span>
                  <p style={{ fontSize: "15px", lineHeight: 1.7, color: "var(--color-cream-soft)", maxWidth: "64ch", margin: 0 }}>{entry.text}</p>
                </div>
              ))}
              <div style={{ borderTop: "1px solid rgba(254,250,239,0.1)" }} />
            </div>
          )}

          {topic === "charter" && lang === "de" && <GesellschaftsvertragDe />}

          {topic === "charter" && lang === "en" && (
            <div className="tl-card-light" style={{ display: "flex", flexDirection: "column", gap: "16px", padding: "44px 40px" }}>
              <p style={{ fontSize: "16px", lineHeight: 1.7, color: "rgba(10,9,7,0.85)", margin: 0 }}>
                Tales&amp;Legends gemeinnützige UG (haftungsbeschränkt) is a German legal entity, and its
                Gesellschaftsvertrag (the notarized founding contract filed with the Amtsgericht Dresden commercial
                register, HRB 48120) is a formal German-language legal document. To avoid any translation
                inaccuracy in a binding legal text, it is published in German only — see the German version of
                this page (use the language switcher above).
              </p>
            </div>
          )}

          {topic === "conduct" &&
            (() => {
              const ConductBody = CONDUCT_CONTENT[lang];
              return <ConductBody />;
            })()}

          {topic === "jugendschutz" &&
            (() => {
              const JugendschutzBody = JUGENDSCHUTZ_CONTENT[lang];
              return (
                <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
                  <JugendschutzBody />
                  <JugendschutzAnhaenge lang={lang} />
                </div>
              );
            })()}

          {topic === "reports" && (
            <div className="tl-card-light" style={{ display: "flex", flexDirection: "column", gap: "16px", padding: "44px 40px" }}>
              {REPORTS[lang].length === 0 ? (
                <p style={{ fontSize: "16px", lineHeight: 1.7, color: "rgba(10,9,7,0.7)", margin: 0 }}>
                  {t("about.reports.empty")}
                </p>
              ) : (
                <ul style={{ display: "flex", flexDirection: "column", gap: "16px", margin: 0, padding: 0, listStyle: "none" }}>
                  {REPORTS[lang].map((report) => (
                    <li key={report.file}>
                      <a href={report.file} target="_blank" rel="noopener noreferrer" style={{ fontSize: "16px" }}>
                        {report.title}
                      </a>
                      {report.description && (
                        <p style={{ fontSize: "14px", lineHeight: 1.6, color: "rgba(10,9,7,0.65)", margin: "4px 0 0" }}>
                          {report.description}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
